import React, { useState, useCallback, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import CognitionAssessment from './CognitionAssessment';
import MobilityAssessment from './MobilityAssessment';
import VitalityAssessment from './VitalityAssessment';
import VisionAssessment from './VisionAssessment';
import HearingAssessment from './HearingAssessment';
import PsychologicalAssessment from './PsychologicalAssessment';
import SocialCareAssessment from './SocialCareAssessment';
import CarerSupportAssessment from './CarerSupportAssessment';
import UrinaryIncontinenceAssessment from './UrinaryIncontinenceAssessment';
import { ComprehensiveAssessmentResult, DomainResult, IntrinsicCapacityResults, KeyFactorsResults, SocialCareResult, UIResult, CarerSupportResult } from '../../types/index';
import { unifiedUserService } from '../../services/unifiedUserService';
import { authService } from '../../services/authService';
import { 
  validateAssessmentStatus, 
  validateCarerAssessmentData, 
  generateAdviceByStatus, 
  safeFilterAssessmentResults,
  assessDataIntegrity
} from '../../utils/assessmentValidation';

interface AssessmentManagerProps {
  onComplete: (result: ComprehensiveAssessmentResult) => void;
  userId?: string; // 被评估用户的ID
  initialStep?: AssessmentStep;
  quickTestMode?: boolean;
}

type AssessmentStep = 
  | 'cognition' | 'mobility' | 'vitality' | 'vision' | 'hearing' | 'psychological' 
  | 'phase_transition'
  | 'social_care' | 'carer_support' | 'urinary_incontinence'
  | 'completed';

// A dedicated component for the transition screen
const PhaseTransition: React.FC<{ onNext: () => void }> = ({ onNext }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onNext();
    }, 3000); // Automatically move to the next step after 3 seconds
    return () => clearTimeout(timer);
  }, [onNext]);

  return (
    <div className="text-center space-y-6 py-12">
      <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">第一阶段完成！</h2>
        <p className="text-lg text-gray-600 mb-4">内在能力评估已完成，接下来进行关键健康因素评估。</p>
      </div>
      <div className="flex justify-center items-center space-x-2">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        <p className="text-sm text-gray-600">准备进入第二阶段...</p>
      </div>
    </div>
  );
};


const AssessmentManager: React.FC<AssessmentManagerProps> = ({
  onComplete,
  userId,
  initialStep = 'cognition',
  quickTestMode = false
}) => {
  const { currentUser } = useUser();

  // MOCK: In a real app, this would come from user profile
  const [hasCarer] = useState(true); 

  const [currentStep, setCurrentStep] = useState<AssessmentStep>(initialStep);
  const [intrinsicCapacityResults, setIntrinsicCapacityResults] = useState<Partial<IntrinsicCapacityResults>>({});
  const [keyFactorsResults, setKeyFactorsResults] = useState<Partial<KeyFactorsResults>>({});
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<1 | 2>(1);

  const phase1Steps: AssessmentStep[] = ['cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychological'];
  const phase2Steps: AssessmentStep[] = ['social_care', 'urinary_incontinence', 'carer_support'];
  const allSteps = [...phase1Steps, ...(hasCarer ? phase2Steps : phase2Steps.filter(step => step !== 'carer_support'))];
  
  const stepTitles = {
    cognition: '认知能力',
    mobility: '行动能力', 
    vitality: '营养状况',
    vision: '视力健康',
    hearing: '听力健康',
    psychological: '心理健康',
    phase_transition: '阶段过渡',
    social_care: '社会照护',
    carer_support: '照护者支持',
    urinary_incontinence: '尿失禁'
  };

  const handleIntrinsicCapacityComplete = useCallback((domain: keyof IntrinsicCapacityResults, result: DomainResult) => {
    console.log(`🔄 ${domain} 评估完成:`, result);
    
    // 数据完整性验证
    if (domain === 'cognition' && result.testResults) {
      const timeTest = result.testResults.find(r => r.name === "时间定向");
      const spaceTest = result.testResults.find(r => r.name === "空间定向");
      const wordTest = result.testResults.find(r => r.name === "三词记忆");
      
      console.log('🔍 认知评估数据完整性检查:');
      console.log('- 时间定向rawUserAnswers:', timeTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      console.log('- 空间定向rawUserAnswers:', spaceTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      console.log('- 词汇记忆rawUserAnswers:', wordTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      
      if (timeTest?.rawUserAnswers) {
        console.log('- 时间定向用户答案:', timeTest.rawUserAnswers.answers);
      }
      if (spaceTest?.rawUserAnswers) {
        console.log('- 空间定向用户答案:', spaceTest.rawUserAnswers.answers);
      }
      if (wordTest?.rawUserAnswers) {
        console.log('- 词汇记忆用户答案:', wordTest.rawUserAnswers.answers);
      }
    }
    
    // 行动能力评估数据完整性验证
    if (domain === 'mobility' && result.testResults) {
      const chairTest = result.testResults.find(r => r.name === "椅子站立测试");
      
      console.log('🔍 行动能力评估数据完整性检查:');
      console.log('- 椅子起立测试rawUserAnswers:', chairTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      console.log('- 椅子起立测试details:', chairTest?.details ? '✅ 有数据' : '❌ 缺失');
      
      if (chairTest?.rawUserAnswers) {
        console.log('- 椅子起立用户数据:', chairTest.rawUserAnswers);
      }
      if (chairTest?.details) {
        console.log('- 椅子起立测试详情:', chairTest.details);
      }
    }
    
    // 营养状况评估数据完整性验证
    if (domain === 'vitality' && result.testResults) {
      const weightTest = result.testResults.find(r => r.name === "体重减轻评估");
      const appetiteTest = result.testResults.find(r => r.name === "食欲减退评估");
      
      console.log('🔍 营养状况评估数据完整性检查:');
      console.log('- 体重减轻rawUserAnswers:', weightTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      console.log('- 食欲减退rawUserAnswers:', appetiteTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      
      if (weightTest?.rawUserAnswers) {
        console.log('- 体重减轻用户数据:', weightTest.rawUserAnswers);
      }
      if (appetiteTest?.rawUserAnswers) {
        console.log('- 食欲减退用户数据:', appetiteTest.rawUserAnswers);
      }
    }
    
    // 心理健康数据完整性验证
    if (domain === 'psychologicalCapacity' && result.testResults) {
      const depressionTest = result.testResults.find(r => r.name === "抑郁情绪评估");
      const anhedoniaTest = result.testResults.find(r => r.name === "兴趣缺失评估");
      
      console.log('🔍 心理健康评估数据完整性检查:');
      console.log('- 抑郁情绪rawUserAnswers:', depressionTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      console.log('- 兴趣缺失rawUserAnswers:', anhedoniaTest?.rawUserAnswers ? '✅ 有数据' : '❌ 缺失');
      
      if (depressionTest?.rawUserAnswers) {
        console.log('- 抑郁情绪用户数据:', depressionTest.rawUserAnswers);
      }
      if (anhedoniaTest?.rawUserAnswers) {
        console.log('- 兴趣缺失用户数据:', anhedoniaTest.rawUserAnswers);
      }
    }
    
    setIntrinsicCapacityResults(prev => ({ ...prev, [domain]: result }));
  }, []);

  const handleSocialCareComplete = useCallback((result: SocialCareResult) => {
    console.log('🔄 socialCareSupport 评估完成:', result);
    
    // 社会照护数据完整性验证
    console.log('🔍 社会照护评估数据完整性检查:');
    console.log('- responses数据:', result.responses ? '✅ 有数据' : '❌ 缺失');
    console.log('- riskAreas数据:', result.riskAreas ? '✅ 有数据' : '❌ 缺失');
    if (result.responses) {
      console.log('- 社会照护用户回答:', result.responses);
    }
    if (result.riskAreas) {
      console.log('- 风险领域:', result.riskAreas);
    }
    
    setKeyFactorsResults(prev => ({ ...prev, socialCareSupport: result }));
  }, []);

  const handleCarerSupportComplete = useCallback((result: CarerSupportResult) => {
    console.log('🔄 carerSupport 评估完成:', result);
    
    // 照护者支持数据完整性验证
    console.log('🔍 照护者支持评估数据完整性检查:');
    console.log('- responses数据:', result.responses ? '✅ 有数据' : '❌ 缺失');
    console.log('- supportNeedsMet:', result.supportNeedsMet ? '✅' : '❌');
    console.log('- hasConfidence:', result.hasConfidence ? '✅' : '❌');
    console.log('- negativeImpacts数据:', result.negativeImpacts ? '✅ 有数据' : '❌ 缺失');
    if (result.responses) {
      console.log('- 照护者支持用户回答:', result.responses);
    }
    if (result.negativeImpacts) {
      console.log('- 负面影响:', result.negativeImpacts);
    }
    
    setKeyFactorsResults(prev => ({ ...prev, carerSupport: result }));
  }, []);

  const handleUrinaryIncontinenceComplete = useCallback((result: UIResult) => {
    console.log('🔄 urinaryIncontinence 评估完成:', result);
    
    // 尿失禁数据完整性验证
    console.log('🔍 尿失禁评估数据完整性检查:');
    console.log('- responses数据:', result.responses ? '✅ 有数据' : '❌ 缺失');
    console.log('- hasProblem:', result.hasProblem !== undefined ? '✅' : '❌');
    if (result.responses) {
      console.log('- 尿失禁用户回答:', result.responses);
    }
    
    setKeyFactorsResults(prev => ({ ...prev, urinaryIncontinence: result }));
  }, []);

  const handleNext = useCallback(() => {
    // 快速测试模式：完成当前测试后直接生成结果
    if (quickTestMode) {
      setCurrentStep('completed');
      return;
    }

    if (currentStep === 'psychological') {
      setCurrentPhase(2);
      setCurrentStep('phase_transition');
      return;
    }

    const currentAllSteps = [...phase1Steps, ...(hasCarer ? phase2Steps : phase2Steps.filter(step => step !== 'carer_support'))];
    const currentIndex = currentAllSteps.indexOf(currentStep);

    if (currentIndex < currentAllSteps.length - 1) {
      setCurrentStep(currentAllSteps[currentIndex + 1]);
    } else {
      setCurrentStep('completed');
      // 不直接调用generateFinalResult，等待useEffect处理
    }
  }, [currentStep, hasCarer, quickTestMode]);

  useEffect(() => {
    const currentAllSteps = [...phase1Steps, ...(hasCarer ? phase2Steps : phase2Steps.filter(step => step !== 'carer_support'))];
    const totalSteps = currentAllSteps.length;
    const currentStepIndex = currentAllSteps.indexOf(currentStep);
    const progressPercentage = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;
    setProgress(progressPercentage);
  }, [currentStep, hasCarer]);

  // 监听评估状态，当所有评估完成时生成最终结果
  useEffect(() => {
    if (currentStep === 'completed') {
      // 快速测试模式：立即生成结果，不等待所有评估完成
      if (quickTestMode) {
        console.log('🚀 快速测试模式：立即生成结果');
        setTimeout(() => generateFinalResult(), 100);
        return;
      }

      // 检查是否所有必要的评估都完成了
      const expectedDomains = ['cognition', 'mobility', 'vitality', 'vision', 'hearing', 'psychologicalCapacity'];
      const expectedKeyFactors = ['socialCareSupport', 'urinaryIncontinence', ...(hasCarer ? ['carerSupport'] : [])];
      
      const completedDomains = expectedDomains.filter(domain => 
        intrinsicCapacityResults[domain as keyof IntrinsicCapacityResults] != null
      );
      const completedKeyFactors = expectedKeyFactors.filter(factor => 
        keyFactorsResults[factor as keyof KeyFactorsResults] != null
      );
      
      console.log('📋 评估完成状态检查:');
      console.log('预期内在能力评估:', expectedDomains);
      console.log('已完成内在能力评估:', completedDomains);
      console.log('预期关键因素评估:', expectedKeyFactors);
      console.log('已完成关键因素评估:', completedKeyFactors);
      
      if (completedDomains.length === expectedDomains.length && 
          completedKeyFactors.length === expectedKeyFactors.length) {
        console.log('✅ 所有评估都已完成，开始生成最终结果');
        // 小延迟以确保状态完全更新
        setTimeout(() => generateFinalResult(), 100);
      } else {
        console.log('⚠️ 还有评估未完成，等待中...');
        console.log('缺失的内在能力评估:', expectedDomains.filter(d => !completedDomains.includes(d)));
        console.log('缺失的关键因素评估:', expectedKeyFactors.filter(f => !completedKeyFactors.includes(f)));
        
        // 添加超时保护机制：如果等待超过10秒，强制生成结果
        setTimeout(() => {
          if (currentStep === 'completed') {
            console.log('🚨 等待超时，强制生成评估结果');
            generateFinalResult();
          }
        }, 10000);
      }
    }
  }, [currentStep, intrinsicCapacityResults, keyFactorsResults, hasCarer, quickTestMode]);


  const generateFinalResult = () => {
    // 调试日志：检查生成最终结果时的数据状态
    console.log('🚀 开始生成最终评估结果');
    console.log('内在能力评估结果:', intrinsicCapacityResults);
    console.log('关键因素评估结果:', keyFactorsResults);
    
    const failedDomains = Object.values(intrinsicCapacityResults).filter(r => r?.status === 'FAIL').length;
    const overallStatus: 'ALL_PASS' | 'SOME_FAIL' | 'MULTIPLE_FAIL' = 
      failedDomains === 0 ? 'ALL_PASS' : failedDomains === 1 ? 'SOME_FAIL' : 'MULTIPLE_FAIL';

    const recommendationCategories: string[] = ['universal_health_advice'];
    if (overallStatus !== 'ALL_PASS') recommendationCategories.push('community_intervention');
    if (overallStatus === 'MULTIPLE_FAIL') recommendationCategories.push('professional_consultation');
    
    // 🔧 使用改进的验证工具修复逻辑漏洞
    
    // 1. 数据完整性检查
    const dataIntegrity = assessDataIntegrity(
      intrinsicCapacityResults,
      keyFactorsResults,
      { hasCarer }
    );

    // 添加数据完整性相关的建议类别
    if (!dataIntegrity.isComplete) {
      const errorIssues = dataIntegrity.issues.filter(issue => issue.severity === 'error');
      if (errorIssues.length > 0) {
        recommendationCategories.push('assessment_incomplete');
      }
    }

    // 2. 安全过滤和状态验证
    const intrinsicEntries = safeFilterAssessmentResults(intrinsicCapacityResults);
    const keyFactorEntries = safeFilterAssessmentResults(keyFactorsResults, {
      customFilter: (key, value) => {
        // 照护者评估的特殊处理逻辑
        if (key === 'carerSupport') {
          const validation = validateCarerAssessmentData(hasCarer, value, key);
          if (!validation.isComplete && validation.recommendation) {
            console.warn(validation.warning);
          }
          return validation.shouldInclude;
        }
        return value !== undefined;
      }
    });

    const allAssessmentResults = [...intrinsicEntries, ...keyFactorEntries];

    // 3. 生成具体建议的状态验证
    const specificRecommendations: string[] = [];
    const statusValidationErrors: string[] = [];

    allAssessmentResults.forEach(([itemName, result]) => {
      if (!result) {
        // 处理缺失数据
        if (itemName === 'carerSupport' && hasCarer) {
          const carerValidation = validateCarerAssessmentData(hasCarer, result, itemName);
          if (carerValidation.recommendation) {
            specificRecommendations.push(carerValidation.recommendation);
          }
        }
        return;
      }

      // 提取状态值（处理不同的数据结构）
      const status = typeof result === 'object' && result !== null && 'status' in result ? result.status : 'UNKNOWN';

      // 验证状态
      const statusValidation = validateAssessmentStatus(status, itemName);
      
      if (!statusValidation.isValid) {
        statusValidationErrors.push(statusValidation.warning || `${itemName}状态验证失败`);
        specificRecommendations.push(`${itemName}评估状态异常，建议重新评估`);
        return;
      }

      // 生成基于状态的建议
      if (statusValidation.status !== 'UNKNOWN') {
        const adviceResult = generateAdviceByStatus(statusValidation.status, itemName);
        specificRecommendations.push(adviceResult.advice);
      }
    });

    // 记录所有验证错误
    if (statusValidationErrors.length > 0) {
      console.warn('评估状态验证错误:', statusValidationErrors);
      recommendationCategories.push('status_validation_errors');
    }

    // 🎯 先获取正确的用户ID
    const currentUserId = userId || currentUser?.id;

    const finalResult: ComprehensiveAssessmentResult = {
      intrinsicCapacity: intrinsicCapacityResults as IntrinsicCapacityResults,
      keyFactors: keyFactorsResults as KeyFactorsResults,
      overallStatus,
      recommendationCategories,
      // 🔧 改进的建议和数据质量信息
      nextSteps: specificRecommendations.length > 0 ? specificRecommendations : ['根据评估结果制定个人化照护计划'],
      // 添加数据完整性报告（可选字段扩展）
      userId: currentUserId || userId, // 使用UserContext的用户ID，用于统计和追踪
      sessionId: `session_${Date.now()}`,
      completedAt: new Date().toISOString(),
      // 在控制台中记录数据质量摘要（用于开发和调试）
      ...(dataIntegrity.issues.length > 0 && {
        consultantRecommended: true // 如果有数据质量问题，建议咨询顾问
      })
    };

    // 在控制台输出数据质量报告（开发环境使用）
    if (process.env.NODE_ENV === 'development') {
      console.group('📊 评估数据质量报告');
      console.log('数据完整性:', dataIntegrity.isComplete ? '✅ 完整' : '⚠️ 不完整');
      console.log('评估摘要:', dataIntegrity.summary);
      if (dataIntegrity.issues.length > 0) {
        console.log('发现的问题:', dataIntegrity.issues);
      }
      if (statusValidationErrors.length > 0) {
        console.log('状态验证错误:', statusValidationErrors);
      }
      console.groupEnd();
    }

    if (currentUserId) {
      console.log('✅ [AssessmentManager] 使用用户ID进行能力评估保存:', currentUserId);
    } else {
      console.warn('⚠️ [AssessmentManager] 未找到有效的用户ID');
    }
    if (currentUserId) {
      try {
        console.log('✅ [AssessmentManager] 保存能力评估结果到用户:', currentUserId);
        // 使用统一用户服务保存评估结果
        unifiedUserService.addAssessmentResult(
          currentUserId,
          finalResult,
          '系统自动保存评估结果',
          '评估系统'
        ).then(() => {
          console.log('✅ 评估结果已保存到用户档案:', currentUserId);
        }).catch((error) => {
          console.error('❌ 保存评估结果到用户档案失败:', error);
          // 即使保存失败也继续完成评估流程
        });
      } catch (error) {
        console.error('❌ 保存评估结果异常:', error);
      }
    } else {
      console.error('❌ [AssessmentManager] 无法获取用户ID，无法保存能力评估结果');
    }

    // 等待一个短暂的延迟以确保所有状态更新完成
    setTimeout(() => {
      console.log('📦 传递最终结果给父组件:', finalResult);
      onComplete(finalResult);
    }, 2000);
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">
          {currentPhase === 1 ? '第一阶段：内在能力评估' : '第二阶段：关键健康因素评估'}
        </span>
        <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      
      {currentPhase === 1 && (
        <div className="flex justify-between mt-4">
          {phase1Steps.map((step, index) => {
            const isCompleted = phase1Steps.indexOf(currentStep) > index;
            const isCurrent = currentStep === step;
            
            return (
              <div key={step} className="flex flex-col items-center w-1/6">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-2 ${
                  isCompleted 
                    ? 'bg-green-500 text-white' 
                    : isCurrent 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-300 text-gray-600'
                }`}>
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={`text-xs text-center ${
                  isCurrent ? 'text-blue-600 font-medium' : 'text-gray-500'
                }`}>
                  {stepTitles[step as keyof typeof stepTitles]}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {currentPhase === 2 && (
        <div className="flex justify-around mt-4">
          {phase2Steps.filter(step => hasCarer || step !== 'carer_support').map((step, index) => {
            const isCompleted = allSteps.indexOf(currentStep) > allSteps.indexOf(step);
            const isCurrent = currentStep === step;
            
            return (
              <div key={step} className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-2 ${
                  isCompleted 
                    ? 'bg-green-500 text-white' 
                    : isCurrent 
                      ? 'bg-purple-500 text-white' 
                      : 'bg-gray-300 text-gray-600'
                }`}>
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span className={`text-xs text-center ${
                  isCurrent ? 'text-purple-600 font-medium' : 'text-gray-500'
                }`}>
                  {stepTitles[step as keyof typeof stepTitles]}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderCurrentAssessment = () => {
    switch (currentStep) {
      case 'cognition': return <CognitionAssessment onComplete={r => handleIntrinsicCapacityComplete('cognition', r)} onNext={handleNext} />;
      case 'mobility': return <MobilityAssessment onComplete={r => handleIntrinsicCapacityComplete('mobility', r)} onNext={handleNext} />;
      case 'vitality': return <VitalityAssessment onComplete={r => handleIntrinsicCapacityComplete('vitality', r)} onNext={handleNext} />;
      case 'vision': return <VisionAssessment onComplete={r => handleIntrinsicCapacityComplete('vision', r)} onNext={handleNext} />;
      case 'hearing': return <HearingAssessment onComplete={r => handleIntrinsicCapacityComplete('hearing', r)} onNext={handleNext} />;
      case 'psychological': return <PsychologicalAssessment onComplete={r => handleIntrinsicCapacityComplete('psychologicalCapacity', r)} onNext={handleNext} />;
      case 'phase_transition': return <PhaseTransition onNext={() => setCurrentStep('social_care')} />;
      case 'social_care': return <SocialCareAssessment onComplete={handleSocialCareComplete} onNext={handleNext} />;
      case 'carer_support': return <CarerSupportAssessment onComplete={handleCarerSupportComplete} onNext={handleNext} />;
      case 'urinary_incontinence': return <UrinaryIncontinenceAssessment onComplete={handleUrinaryIncontinenceComplete} onNext={handleNext} />;
      case 'completed': return <div className="text-center py-12"><h2>评估完成！</h2><p>正在生成报告...</p></div>;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentStep !== 'completed' && renderProgressBar()}
        {renderCurrentAssessment()}
      </div>
    </div>
  );
};

export default AssessmentManager;

